import { useState } from "react";
import { Modal } from "semantic-ui-react";

import { CustomerNoteModalForm } from "components/CustomerModal/components/CustomerNotes/components/CustomerNoteModal/components";
import "components/CustomerModal/components/CustomerNotes/components/CustomerNoteModal/CustomerNoteModal.scss";
import { useCreateCustomerNoteMutation, useDeleteCustomerNoteMutation, useUpdateCustomerNoteMutation } from "components/CustomerModal/components/CustomerNotes/hooks";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { toISOString } from "utils/dateHelperFunctions";

import type { Customer, CustomerNote, CustomerNoteAttachment, DealerLocation } from "models";

export type CustomerNoteModalFormValues = {
  note: string;
  attachments: CustomerNoteAttachment[];
  visible_on_wo: boolean;
  visible_to_mechanic: boolean;
};

type CustomerNoteModalProps = {
  open: boolean;
  customer: Customer;
  selectedCustomerNote: CustomerNote | null;
  location?: DealerLocation | null;
  onUnselectCustomerNote: () => void;
  onCloseCreateCustomerNoteModal?: () => void;
};

const FormHelper = createFormHelper<CustomerNoteModalFormValues>();

export const CustomerNoteModal = ({ open, customer, location, selectedCustomerNote, onUnselectCustomerNote, onCloseCreateCustomerNoteModal }: CustomerNoteModalProps) => {
  const [hasValidationError, setHasValidationError] = useState(false);

  const { user } = usePermanentRealTimeUser();
  const { mutateAsync: createCustomerNote, isPending: isCreateCustomerNotePending } = useCreateCustomerNoteMutation();
  const { mutateAsync: deleteCustomerNote, isPending: isDeleteCustomerNotePending } = useDeleteCustomerNoteMutation();
  const { mutateAsync: updateCustomerNote, isPending: isUpdateCustomerNotePending } = useUpdateCustomerNoteMutation();

  const handleModalClose = () => {
    setHasValidationError(false);
    onUnselectCustomerNote();
    onCloseCreateCustomerNoteModal?.();
  };

  const validateForm = (values: CustomerNoteModalFormValues) => {
    setHasValidationError(!values.note);
    return !values.note;
  };

  const handleSubmit = async (values: CustomerNoteModalFormValues) => {
    if (validateForm(values) || !user) return;
    const todayDate = toISOString(new Date());

    if (selectedCustomerNote) await updateCustomerNote({ ...selectedCustomerNote, ...values, updated_by: user, updated_by_id: user.id, updated_on: todayDate });
    else
      await createCustomerNote({
        id: -1,
        ...values,
        dms_nr: null,
        created_on: todayDate,
        updated_on: todayDate,
        created_by: user,
        created_by_id: user.id,
        updated_by_id: user.id,
        customer_id: customer.id
      });

    handleModalClose();
  };

  const handleDeleteCustomerNote = async () => {
    await deleteCustomerNote({ customer_note_id: selectedCustomerNote?.id ?? -1, customer_id: selectedCustomerNote?.customer_id ?? -1 });
    handleModalClose();
  };

  return (
    <Modal open={open} className="CustomerNoteModal">
      <FormHelper.Form
        onSubmit={handleSubmit}
        defaultValues={{
          note: selectedCustomerNote?.note ?? "",
          attachments: selectedCustomerNote?.attachments ?? [],
          visible_on_wo: selectedCustomerNote?.visible_on_wo ?? false,
          visible_to_mechanic: selectedCustomerNote?.visible_to_mechanic ?? false
        }}
      >
        <CustomerNoteModalForm
          customer={customer}
          location={location}
          hasErrors={hasValidationError}
          isPending={isCreateCustomerNotePending || isUpdateCustomerNotePending}
          isDeletePending={isDeleteCustomerNotePending}
          selectedCustomerNote={selectedCustomerNote}
          onClose={handleModalClose}
          onSubmit={handleSubmit}
          onDelete={handleDeleteCustomerNote}
        />
      </FormHelper.Form>
    </Modal>
  );
};
